import { useCallback, useState } from 'react';
import type React from 'react';

import { Separator } from '@change/ds-core-ui/Separator';
import { CheckIcon } from '@change/ds-icons/CheckIcon';
import { ChevronLeftIcon } from '@change/ds-icons/ChevronLeftIcon';

import { Translate } from '@change-corgi/core-react/i18n';
import { Link } from '@change-corgi/core-react/router';

import { ResponsiveBox } from '#src/app/pages/petitionDetails/shared/components';
import { useDmFeedUrls } from '#src/app/pages/petitionDetails/shared/hooks';
import { useLoginStateAsync } from '#src/app/shared/hooks/session';
import { isLoaded } from '#src/app/shared/utils/async';

export const DmFeedBanner: React.FC<EmptyProps> = () => {
	// TODO: This logic is very permissive—any logged-in user with the `r` query param set
	// to a URL will see this banner, and the banner will link to that URL. We could
	// probably make this logic a bit more airtight.
	// https://change.atlassian.net/browse/FE-572
	const loginState = useLoginStateAsync();
	const { feedUrl, responseRequestUrl } = useDmFeedUrls();
	const [hasSentResponseRequest, setHasSentResponseRequest] = useState(false);
	const handleResponseRequestClick = useCallback(() => setHasSentResponseRequest(true), [setHasSentResponseRequest]);

	// This could perform a layout shift but it's a very unusual edge case
	if (!isLoaded(loginState) || loginState.value === 'GUEST' || !feedUrl) return null;

	return (
		<ResponsiveBox viewportSize="normal" className="bg-fill-brand-strong text-typography-inverse-strong-fixed">
			<div className="container flex h-[54px] items-center">
				<Link to={feedUrl} className="flex items-center text-inherit no-underline hover:underline">
					<ChevronLeftIcon size={28} className="mr-2" />
					<Translate value="fe.components.petition_decision_maker_banner.back_link" />
				</Link>
				{responseRequestUrl && (
					<div className="ml-8 flex items-center" data-testid="dm-response-request">
						{hasSentResponseRequest ? (
							<>
								<CheckIcon size={16} className="mr-1" />
								<Translate value="fe.components.petition_decision_maker_response_request.acknowledgement" />
							</>
						) : (
							<Link
								to={responseRequestUrl}
								className="text-inherit no-underline hover:underline"
								onClick={handleResponseRequestClick}
							>
								<Translate value="fe.components.petition_decision_maker_response_request.request_to_respond" />
							</Link>
						)}
					</div>
				)}
			</div>
			<Separator role="presentation" />
		</ResponsiveBox>
	);
};
